import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type { Connector, ConnectorProduct } from '../api/types'
import { Card, EmptyState, ErrorBanner, formatDateTime, Spinner } from '../components/common'
import ConnectorForm from '../components/ConnectorForm'
import { DataTable, type Column } from '../components/DataTable'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'

/**
 * L'elenco dei collegamenti verso i sistemi di destinazione, e il modulo per
 * aggiungerne uno.
 *
 * Il modulo lo costruisce il catalogo, non questo file: quali campi servono per
 * ciascun prodotto lo dichiara l'adattatore. Quando Matrix42 cambia il modo di
 * autenticarsi — l'ha già fatto, passando dal nome utente all'API Token — la
 * modifica resta in un posto solo, e questa pagina non se ne accorge.
 */
export default function Connectors() {
  const connectors = useAsync(() => api.connectors(), [])
  const catalog = useAsync(() => api.connectorCatalog(), [])

  const [kind, setKind] = useState('')

  const products = catalog.data?.products ?? []
  const product: ConnectorProduct | undefined = products.find((p) => p.kind === kind)

  useEffect(() => {
    if (!kind && products.length) setKind(products[0].kind)
  }, [products, kind])

  const colonne: Column<Connector>[] = [
    { key: 'name', header: t('Nome'), render: (c) => <Link to={`/connectors/${c.id}`}>{c.name}</Link> },
    { key: 'product', header: t('Prodotto') },
    { key: 'base_url', header: t('Indirizzo'), render: (c) => <span className="text-muted">{c.base_url}</span> },
    { key: 'execution', header: t('Da dove'), value: (c) => (c.execution === 'probe' ? c.appliance_name ?? t('sonda mancante') : t('cloud')), render: (c) => (
      c.execution === 'probe' ? (
        <span className="badge badge--info">{c.appliance_name ?? t('sonda mancante')}</span>
      ) : (
        <span className="text-muted">{t('cloud')}</span>
      )
    ) },
    { key: 'test', header: t('Collegamento'), value: (c) => (c.last_test_ok === null ? t('mai provato') : c.last_test_ok ? t('riuscito') : t('fallito')), render: (c) => <TestBadge connector={c} /> },
    { key: 'object_types', header: t('Oggetti trovati'), type: 'number', align: 'left', value: (c) => (c.scanned_at ? c.object_types : null) },
    { key: 'mappings', header: t('Mappature'), type: 'number', align: 'left' },
  ]

  return (
    <>
      <div className="page__header">
        <h1 className="page__title">{t('Connettori')}</h1>
        <p className="page__subtitle">
          {t("Collegamenti verso i sistemi di destinazione. Il flusso segue una sequenza controllata: configurazione dell’accesso, scansione del sistema, definizione del mapping, dry-run, sincronizzazione e pianificazione.")}
        </p>
      </div>

      <ErrorBanner error={connectors.error ?? catalog.error} />

      <Card title={t('Nuovo connettore')}>
        {catalog.loading ? (
          <Spinner />
        ) : (
          <>
            <label className="field" style={{ maxWidth: 320 }}>
              <span>{t('Prodotto')}</span>
              <select value={kind} onChange={(e) => setKind(e.target.value)}>
                {products.map((p) => (
                  <option key={p.kind} value={p.kind}>
                    {p.product}
                  </option>
                ))}
              </select>
            </label>
            {/* Il prodotto si sceglie solo in creazione: cambiarlo dopo
                vorrebbe dire un connettore diverso con le mappature del
                precedente, cioè campi che puntano a oggetti che non esistono. */}
            <ConnectorForm product={product} onSaved={() => connectors.reload()} />
          </>
        )}
      </Card>

      <Card title={t('Collegamenti configurati')}>
        {connectors.loading ? (
          <Spinner />
        ) : !connectors.data?.length ? (
          <EmptyState
            title={t('Nessun connettore')}
            hint={t('Configura un collegamento qui sopra: dopo averne verificato la connettività, potrai eseguire la scansione del sistema di destinazione.')}
          />
        ) : (
          <DataTable
            rows={connectors.data}
            columns={colonne}
            rowKey={(c) => c.id}
            conteggio={{ nome: t('connettori') }}
          />
        )}
      </Card>
    </>
  )
}

/**
 * Lo stato del collegamento, con **quando** è stato verificato.
 *
 * Un «riuscito» senza data è la cosa più fuorviante che questa colonna possa
 * dire: la password è cambiata la settimana scorsa, il badge è ancora verde, e
 * nessuno lo mette in dubbio finché la sincronizzazione notturna non fallisce.
 */
function TestBadge({ connector }: { connector: Connector }) {
  const when = useMemo(() => formatDateTime(connector.last_test_at), [connector.last_test_at])
  if (connector.last_test_ok === null) {
    return <span className="badge badge--muted">{t('mai provato')}</span>
  }
  return (
    <>
      <span className={connector.last_test_ok ? 'badge badge--ok' : 'badge badge--error'}>
        {connector.last_test_ok ? t('riuscito') : t('fallito')}
      </span>{' '}
      <span className="text-muted">{when}</span>
    </>
  )
}
